import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MoveHorizontal, Compass, Sparkles } from 'lucide-react';

export const BeforeAfterSlider: React.FC = () => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      handleMove(e.clientX);
    }
  };

  return (
    <section id="philosophy" className="py-24 px-6 md:px-12 max-w-7xl mx-auto relative z-10 scroll-mt-24">
      {/* Section Header */}
      <div className="text-center max-w-2xl mx-auto space-y-3 mb-12">
        <div className="inline-flex items-center gap-2 text-xs font-mono text-arch-accent tracking-widest uppercase">
          <Sparkles className="w-3.5 h-3.5" />
          <span>02 // Tectonic Philosophy & Precision</span>
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white tracking-tight">
          Concept <span className="italic font-normal text-arch-limestone">to</span> Construction
        </h2>
        <p className="text-xs sm:text-sm text-neutral-400 font-light leading-relaxed">
          &ldquo;Architecture is an act of dialogue between topography, tactile materiality, and light.&rdquo; Drag the interactive split handle below to inspect our fidelity from CAD schematic drafting to built reality.
        </p>
      </div>

      {/* Interactive Slider Frame */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchMove={handleTouchMove}
        className="relative aspect-[16/9] md:aspect-[21/9] rounded-sm overflow-hidden border border-white/15 bg-black select-none cursor-ew-resize shadow-2xl"
      >
        {/* Under layer: Completed Built Structure */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=85"
            alt="Completed Monolithic Residence"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-6 right-6 z-10 px-3.5 py-1.5 rounded bg-[#0A0B0D]/85 backdrop-blur border border-white/10 text-xs font-mono text-white tracking-wider flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>BUILT MONOLITH // AS REALIZED</span>
          </div>
        </div>

        {/* Over layer: Technical Schematic / Wireframe Blueprint (Clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
        >
          <div className="relative w-full h-full" style={{ width: containerRef.current ? `${containerRef.current.clientWidth}px` : '100vw' }}>
            <img
              src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=2000&q=85"
              alt="Architectural Blueprint Draft"
              className="w-full h-full object-cover filter invert contrast-125"
            />
            {/* Blueprint Grid Overlay */}
            <div className="absolute inset-0 bg-[#0A192F]/60 mix-blend-multiply" />
            <div className="absolute top-6 left-6 z-10 px-3.5 py-1.5 rounded bg-[#0A192F]/90 backdrop-blur border border-arch-cyan/40 text-xs font-mono text-arch-cyan tracking-wider flex items-center gap-2">
              <Compass className="w-3.5 h-3.5 text-arch-cyan" />
              <span>CAD WIREFRAME // TECHNICAL SCHEMATIC</span>
            </div>
          </div>
        </div>

        {/* Draggable Divider Line & Handle */}
        <div
          className="absolute top-0 bottom-0 z-20"
          style={{ left: `${sliderPosition}%` }}
          onMouseDown={handleMouseDown}
        >
          <div className="w-[2px] h-full bg-arch-accent shadow-[0_0_12px_rgba(197,168,128,0.8)] -translate-x-1/2" />
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#0A0B0D] border-2 border-arch-accent flex items-center justify-center text-arch-accent shadow-xl">
            <MoveHorizontal className="w-4 h-4" />
          </div>
        </div>
      </motion.div>

      {/* Slider Metric Status */}
      <div className="mt-4 flex items-center justify-between text-[11px] font-mono text-arch-muted">
        <span>SCHEMATIC COMPLIANCE: 99.4%</span>
        <span>DRAG SLIDER TO REVEAL TRANSFORMATION</span>
        <span>PROJECT: MONOLITH PAVILION</span>
      </div>
    </section>
  );
};
